import { isNullOrUndefined } from '../../utils/utils';
import {
    AddToCartProp,
    CartAddError,
    CartLineItemAdd,
} from '../../models/Cart';
import useCartUpdate from './useCartUpdate';
import useCart from './useCart';
import SnackbarUtils from '../../utils/SnackbarUtils';
import useItemsAddedDialog from '../dialogs/useItemsAddedDialog';

const useHandleBulkProductsToCart = () => {
    const cart = useCart();
    const cartUpdate = useCartUpdate();
    const itemsAddedDialog = useItemsAddedDialog();

    const createCartLineItem = (product: AddToCartProp): CartLineItemAdd => {
        return {
            merchandiseId:
                'gid://shopify/ProductVariant/' + product.shopify_variant_id,
            quantity: product.quantity == 0 ? 1 : product.quantity,
        };
    };

    const fetchProductsForCart = (
        productList: AddToCartProp[]
    ): CartLineItemAdd[] => {
        return productList
            .filter(
                (elem) =>
                    elem.lineType == 'Item' &&
                    !isNullOrUndefined(elem.shopify_variant_id)
            )
            .map((elem) => {
                return createCartLineItem(elem);
            });
    };

    const checkShopifyProducts = (
        productList: AddToCartProp[]
    ): Promise<CartLineItemAdd[]> => {
        return new Promise((resolve, reject) => {
            const products = fetchProductsForCart(productList);
            if (!isNullOrUndefined(products) && products.length > 0) {
                resolve(products);
            } else {
                reject(new Error('Nincs shopify-os termék a listában'));
            }
        });
    };

    const getAvailableProducts = (
        errors: CartAddError[],
        productList: AddToCartProp[]
    ): CartLineItemAdd[] => {
        return productList
            .filter((elem) => {
                return !errors.find((error) => {
                    return error.message.includes(
                        elem.shopify_variant_id.toString()
                    );
                });
            })
            .map((elem) => {
                return createCartLineItem(elem);
            });
    };

    const setCartConfirmDialog = (
        resp,
        products: AddToCartProp[],
        shopifyCartLines: CartLineItemAdd[]
    ): void => {
        // check if products added successfully
        if (isNullOrUndefined(resp.cartLinesAdd.cart)) {
            const availableProducts = getAvailableProducts(
                resp.cartLinesAdd.userErrors,
                products
            );
            if (availableProducts.length > 0) {
                cart.addProductsToCart(availableProducts).then((result) => {
                    if (isNullOrUndefined(result.cartLinesAdd.cart)) {
                        //error
                        SnackbarUtils.error(
                            'Hiba történt a kosárba rakás közben.'
                        );
                    } else {
                        itemsAddedDialog.showDialog(true, 'partially');
                        cartUpdate.updateCart();
                    }
                });
            } else {
                itemsAddedDialog.showDialog(true, 'none');
            }
        } else {
            if (shopifyCartLines.length < products.length) {
                itemsAddedDialog.showDialog(true, 'partially');
            } else {
                itemsAddedDialog.showDialog(true, 'all');
            }

            cartUpdate.updateCart();
        }
    };

    const handleAddToCart = (
        products: AddToCartProp[],
        translatePrefix: string,
        translateNS: string,
        handleGoToCart?: () => void
    ) => {
        itemsAddedDialog.setDialog(
            translatePrefix,
            translateNS,
            handleGoToCart
        );
        checkShopifyProducts(products)
            .then((shopifyCartLines) => {
                cart.addProductsToCart(shopifyCartLines)
                    .then((shopifyResp) => {
                        setCartConfirmDialog(
                            shopifyResp,
                            products,
                            shopifyCartLines
                        );
                    })
                    .catch(() => {
                        itemsAddedDialog.showDialog(true, 'none');
                    });
            })
            .catch(() => {
                itemsAddedDialog.showDialog(true, 'none');
            });
    };

    return {
        handleAddToCart,
    };
};

export default useHandleBulkProductsToCart;
